import {useState, useEffect} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useWorkspace} from '../hooks/useWorkspace';
import {dataClient} from '../data/dataClient';
import {emitShareFrame} from '../services/shareBus';
import ChatWindow from '../components/ChatWindow';
import ApprovalGate from '../components/ApprovalGate';

const {FiGlobe, FiPlus, FiMessageSquare, FiFileText, FiEye, FiCheck, FiX} = FiIcons;

export default function Website() {
  const state = useWorkspace();
  const websitePages = state.websitePages || [];

  const [selectedId, setSelectedId] = useState(websitePages[0]?.id || null);
  const [editingBlocks, setEditingBlocks] = useState({});
  const [savedFeedback, setSavedFeedback] = useState({});
  const [showAddPage, setShowAddPage] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPagePath, setNewPagePath] = useState('');
  const [previewMode, setPreviewMode] = useState(false);

  // Website team Lead: Iris (Web Strategist)
  const lead = state.bots.find((b) => b.slug === 'web-strategist' || b.id === 'bot_web')
    || state.bots.find((b) => b.teamId === 'team_web' && b.isLead)
    || state.bots.find((b) => b.isLead && b.enabled);

  const selected = websitePages.find((page) => page.id === selectedId) || websitePages[0] || null;

  useEffect(() => {
    if (!selectedId && websitePages.length) {
      setSelectedId(websitePages[0].id);
    }
  }, [websitePages, selectedId]);

  // Sync block copies into controlled edit state
  useEffect(() => {
    if (selected?.blocks) {
      const initial = {};
      selected.blocks.forEach((blk) => {
        initial[blk.id] = blk.copy || '';
      });
      setEditingBlocks(initial);
    }
  }, [selected?.id, selected?.blocks]);

  async function handleBlockCopyChange(blockId, text) {
    setEditingBlocks((prev) => ({...prev, [blockId]: text}));
  }

  async function saveBlockCopy(blockId) {
    if (!selected) return;
    const newText = editingBlocks[blockId] ?? '';
    const blocks = (selected.blocks || []).map((item) => (
      item.id === blockId ? {...item, copy: newText} : item
    ));

    await dataClient.update('websitePages', selected.id, {blocks});
    // T12 live share: mirror website structure to Viewer-Guests (read-only).
    emitShareFrame({
      screenSlug: 'website',
      payload: {
        kind: 'website',
        title: selected.title,
        blocks: blocks.map((blk) => ({ type: blk.type, copy: blk.copy || '', status: blk.status }))
      }
    });
    setSavedFeedback((prev) => ({...prev, [blockId]: true}));
    setTimeout(() => {
      setSavedFeedback((prev) => ({...prev, [blockId]: false}));
    }, 2500);
  }

  async function updateBlockStatus(blockId, newStatus) {
    if (!selected) return;
    const currentCopy = editingBlocks[blockId];
    const blocks = (selected.blocks || []).map((item) => (
      item.id === blockId
        ? {...item, status: newStatus, ...(currentCopy !== undefined ? {copy: currentCopy} : {})}
        : item
    ));

    await dataClient.update('websitePages', selected.id, {blocks});
  }

  async function handleCreatePage(event) {
    event.preventDefault();
    if (!newPageTitle.trim()) return;

    let path = newPagePath.trim();
    if (!path.startsWith('/')) path = `/${path}`;

    const newPage = await dataClient.create('websitePages', {
      title: newPageTitle.trim(),
      path,
      status: 'draft',
      blocks: [
        {
          id: `blk_${Date.now()}`,
          name: 'Main Section',
          status: 'draft',
          copy: 'Draft page copy crafted with Iris & Finn…',
          source_refs: null
        }
      ]
    });

    setSelectedId(newPage.id);
    setNewPageTitle('');
    setNewPagePath('');
    setShowAddPage(false);
  }

  return (
    <div className="page">
      <header className="page-header">
        <span className="eyebrow">Website</span>
        <h1>Shape, review, then publish.</h1>
        <p>Dual-pane workspace: strategist chat on the left, live structure and approval gates on the right.</p>
      </header>

      {/* Add Page Modal */}
      {showAddPage && (
        <div className="add-page-modal-backdrop" onClick={() => setShowAddPage(false)}>
          <div className="add-page-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="section-heading">
              <h3>Create new page</h3>
              <button type="button" className="icon-button" onClick={() => setShowAddPage(false)}>
                <SafeIcon icon={FiX} />
              </button>
            </div>
            <form onSubmit={handleCreatePage}>
              <label className="filter">
                Page title
                <input
                  value={newPageTitle}
                  onChange={(e) => setNewPageTitle(e.target.value)}
                  placeholder="e.g. Services"
                  required
                />
              </label>
              <label className="filter" style={{marginTop: '12px'}}>
                URL Path
                <input
                  value={newPagePath}
                  onChange={(e) => setNewPagePath(e.target.value)}
                  placeholder="e.g. /services"
                  required
                />
              </label>
              <div className="wizard-actions">
                <button type="button" className="button ghost" onClick={() => setShowAddPage(false)}>
                  Cancel
                </button>
                <button type="submit" className="button primary" disabled={!newPageTitle.trim()}>
                  Create page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dual Pane Layout */}
      <div className="website-dual-pane">
        {/* Left Pane: Iris Lead chat (consulting Finn) */}
        <section className="website-chat-pane">
          <ChatWindow
            module="website"
            lead={lead}
            messages={state.messages?.website || []}
            planLabel="Save page brief as Plan"
            onPlan={() => {}}
          />
        </section>

        {/* Right Pane: Live structure, block editor & approval gate */}
        <section className="panel website-structure-pane">
          {/* Page Tabs */}
          <div className="page-tabs-bar">
            {websitePages.map((page) => (
              <button
                key={page.id}
                type="button"
                className={`page-tab-btn ${selected?.id === page.id ? 'active' : ''}`}
                onClick={() => setSelectedId(page.id)}
              >
                <SafeIcon icon={FiGlobe} />
                <span>{page.title}</span>
                <span className={`status ${page.status || 'published'}`}>{page.status || 'published'}</span>
              </button>
            ))}
            <button
              type="button"
              className="button secondary compact"
              onClick={() => setShowAddPage(true)}
              title="Add a new page to the website"
            >
              <SafeIcon icon={FiPlus} />
              Add page
            </button>
          </div>

          {selected ? (
            <div className="page-editor-container">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Page editor · {selected.path}</span>
                  <h2>{selected.title}</h2>
                </div>
                <button
                  type="button"
                  className={`button ${previewMode ? 'primary' : 'secondary'} compact`}
                  onClick={() => setPreviewMode(!previewMode)}
                >
                  <SafeIcon icon={FiEye} />
                  {previewMode ? 'Exit preview' : 'Live preview'}
                </button>
              </div>

              {/* Blocks */}
              {(selected.blocks || []).map((block) => {
                const sourceMsg = block.source_refs?.messageId
                  ? (state.messages?.website || []).find((m) => m.id === block.source_refs.messageId)
                  : null;
                const sourcePlan = block.source_refs?.planId
                  ? (state.plans || []).find((p) => p.id === block.source_refs.planId)
                  : null;

                if (previewMode) {
                  return (
                    <article className="block-editor preview-mode" key={block.id}>
                      <div className="inline spread">
                        <b>{block.name}</b>
                        <span className={`status ${block.status}`}>{block.status}</span>
                      </div>
                      <div className="preview-rendered-box" style={{padding: '16px', background: 'var(--color-surfaceAlt)', borderRadius: 'var(--radius-sm)', margin: '12px 0'}}>
                        <p style={{whiteSpace: 'pre-wrap', lineHeight: 1.6}}>{editingBlocks[block.id] ?? block.copy}</p>
                      </div>
                    </article>
                  );
                }

                return (
                  <article className="block-editor" key={block.id}>
                    <div className="inline spread">
                      <div className="inline">
                        <b>{block.name}</b>
                        <span className={`status ${block.status}`}>{block.status}</span>
                        {savedFeedback[block.id] && (
                          <span className="block-save-feedback">
                            <SafeIcon icon={FiCheck} /> Saved
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="button ghost compact"
                        onClick={() => saveBlockCopy(block.id)}
                        title="Save copy edits"
                      >
                        Save edits
                      </button>
                    </div>

                    {/* Bidirectional Traceability: block → source chat message + Plan */}
                    {(sourceMsg || sourcePlan) && (
                      <div className="traceability-row">
                        {sourceMsg && (
                          <span
                            className="trace-chip"
                            title={`Produced from Iris chat message: "${sourceMsg.text.slice(0, 60)}…"`}
                          >
                            <SafeIcon icon={FiMessageSquare} />
                            Source: Iris chat
                          </span>
                        )}
                        {sourcePlan && (
                          <span
                            className="trace-chip"
                            title={`Derived from Plan: "${sourcePlan.title}"`}
                          >
                            <SafeIcon icon={FiFileText} />
                            Source Plan: {sourcePlan.title}
                          </span>
                        )}
                      </div>
                    )}

                    <textarea
                      value={editingBlocks[block.id] ?? (block.copy || '')}
                      onChange={(e) => handleBlockCopyChange(block.id, e.target.value)}
                      onBlur={() => saveBlockCopy(block.id)}
                      placeholder="Enter block copy…"
                    />

                    {/* Shared ApprovalGate Component */}
                    <ApprovalGate
                      status={block.status}
                      onApprove={() => updateBlockStatus(block.id, 'published')}
                      onRevoke={() => updateBlockStatus(block.id, 'draft')}
                      title={block.status === 'draft' ? 'Approval required before live publish' : 'Signed off and published'}
                      description={
                        block.status === 'draft'
                          ? 'This block was shaped by Iris & Finn. It requires explicit workspace owner approval before going live to visitors.'
                          : 'This block is approved and live on your public site.'
                      }
                    />
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="empty-copy">Select or create a page to view blocks.</div>
          )}
        </section>
      </div>
    </div>
  );
}
